/**
 * Free Knowledge Source Service (NQG-RQ-26 v2)
 * API calls for the isolated free-source module: consolidation (sync) of the
 * PDF question bank into the main question bank + topic-identification status.
 */

import axios from '../utils/axios';

const API_URL = '/free-source';

// ── Response types ───────────────────────────────────────────────────────────

export interface FreeSourceSyncResult {
  pdfTotal: number;
  created: number;
  updated: number;
  upToDate: number;
  skipped: Array<{ id: string; reason: string }>;
  orphansRemoved: number;
  failed: Array<{ id: string; error: string }>;
  warnings: string[];
  ranAt: string;
  durationMs: number;
}

export interface FreeSourceSyncStatus {
  pdfTotal: number;
  pdfSupported: number;
  mirrored: number;
  lastSyncedAt: string | null;
  syncInFlight: boolean;
}

export interface FreeSourceTopicStatus {
  analysisId: string;
  filename: string;
  status: string;
  totalItems: number;
  extractionMode: string;
  identified: boolean;
  /** pt-BR message explaining why topics could not be identified. */
  reason?: string;
  error?: string;
}

export interface FreeSourceRescueResult {
  analysisId: string;
  filename: string;
  itemsBefore: number;
  itemsAfter: number;
  usedFallbackSingleTopic: boolean;
  chunksProcessed: number;
}

// ── Service ──────────────────────────────────────────────────────────────────

class FreeSourceService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Run a full reconciliation of the PDF question bank into the main bank.
   * Returns the sync summary. Backend responds 409 if a sync is already running.
   */
  async runSync(): Promise<FreeSourceSyncResult> {
    const response = await axios.post(
      `${API_URL}/sync`,
      {},
      { headers: this.getAuthHeaders(), timeout: 300000 } // reconciliation can be slow
    );
    return response.data.data;
  }

  /**
   * Get the current sync status (mirrored counts, last sync time, in-flight flag).
   */
  async getSyncStatus(): Promise<FreeSourceSyncStatus> {
    const response = await axios.get(`${API_URL}/sync/status`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Get the topic-identification status for a single analysis
   * (includes a pt-BR reason when no topics were identified).
   */
  async getTopicStatus(analysisId: string): Promise<FreeSourceTopicStatus> {
    const response = await axios.get(`${API_URL}/analyses/${analysisId}/topic-status`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Relaxed re-extraction ("rescue") for an analysis with no identified
   * topics. The same PDF must be re-uploaded (raw text is not stored).
   * Runs a permissive extraction that always yields at least one topic.
   */
  async rescueAnalysis(analysisId: string, file: File): Promise<FreeSourceRescueResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/analyses/${analysisId}/rescue`, formData, {
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // multiple LLM passes — can take a few minutes
    });
    return response.data.data;
  }
}

export const freeSourceService = new FreeSourceService();
